// Una imagen del chat, por su id, sin que el agente tenga que saber de qué TIPO es —
// ADR-2026-09-22-mirar-una-imagen-no-depende-de-su-tipo.
//
// El agente conoce media docena de ids: el producto del catálogo, la modelo guardada, la
// pose, el fondo, la creación que salió de una generación, la foto que el usuario subió
// suelta y la del montón. Para el sistema son siete tablas y tres almacenamientos; para
// ÉL son la misma cosa: una foto que quiere mirar antes de decidir. Que tuviera que
// acertar el parámetro correcto según el origen era una filtración de la implementación,
// y el costo real es que elegía a ciegas: mandaba "usá a Lucía" sin haber visto a Lucía.
//
// Acá la resolución es por PRUEBA, en orden de costo: primero la forma del id (gratis),
// después las listas que ya están en memoria, y al final las que piden una consulta. El
// primero que contesta gana. Dos entidades distintas con el mismo id es imposible en la
// práctica (son uuids, salvo los modelos que son enteros y se prueban antes).
//
// Lo que NO entra acá son los CONJUNTOS: un producto con sus cuatro vistas
// (`ver_productos`), una carpeta de moodboard (`ver_moodboard`), un PDF (`ver_pdf`) y un
// video (`extraer_frames`) se miran distinto porque SON distintos —hay que paginar,
// elegir vistas o renderizar—. La separación que queda es por comportamiento, no por tipo.

/** De dónde salió la imagen. Decide sólo el rótulo y cómo se pasa como referencia. */
export type TipoDeImagen =
  | 'adjunto'
  | 'monton'
  | 'producto'
  | 'modelo'
  | 'generada'
  | 'pose'
  | 'fondo';

/** Una imagen ya resuelta, lista para el bloque. */
export interface ImagenResuelta {
  id: string;
  /** null = se supo cuál era pero no se pudo cargar; el bloque la nombra igual. */
  url: string | null;
  tipo: TipoDeImagen;
  /** El nombre que el usuario le puso, cuando la fuente lo tiene (producto, modelo). */
  nombre?: string;
}

/** Lo que devuelve una fuente: la URL (o null si está pero no se pudo cargar) y el
 *  nombre si lo tiene. `null` significa "este id no es mío". */
export type RespuestaDeFuente = { url: string | null; nombre?: string } | null;

/**
 * De dónde se saca cada tipo. Se inyectan para poder probar el ORDEN y el corte sin
 * Supabase; el hook las arma con lo que ya tiene a mano.
 */
export type FuentesDeImagen = Partial<Record<TipoDeImagen, (id: string) => Promise<RespuestaDeFuente>>>;

/**
 * El orden de prueba, de más barato a más caro:
 *  • `adjunto` y `monton` se reconocen por la FORMA del id y no consultan nada;
 *  • `modelo` es un entero, así que un id numérico se prueba ahí primero;
 *  • `producto` sale de la lista que ya viaja en el prompt;
 *  • `generada`, `pose` y `fondo` son uuids y piden una consulta.
 */
const ORDEN: TipoDeImagen[] = ['adjunto', 'monton', 'modelo', 'producto', 'generada', 'pose', 'fondo'];

// `web-…`: una imagen que el agente trajo de una página (ADR-2026-10-15-el-agente-abre-paginas-web);
// `pdf-…`: una foto sacada de un PDF adjunto (ADR-2026-10-18-el-agente-saca-las-fotos-de-un-pdf);
// `marca-…`: un elemento de marca que miró con `ver_elementos_de_marca` (un logo, para pegarlo con
// el editor de imágenes — ADR-2026-11-02-el-agente-edita-imagenes-con-ffmpeg). Viven con los
// adjuntos del chat y se resuelven igual (`adjuntoPorId` ya los conocía).
export const esIdDeAdjunto = (id: string): boolean =>
  /^m\d+-\d+$/.test(id) || /^(?:web|pdf|marca)-[0-9a-f]{12}$/.test(id);
export const esIdDelMonton = (id: string): boolean => /^f\d+$/.test(id);
const esEntero = (id: string): boolean => /^\d+$/.test(id);

/** Qué fuentes tiene sentido probar para este id. Ahorra consultas: un "m1-2" no se va a
 *  buscar nunca en la tabla de poses, y un uuid no es un modelo. */
function candidatos(id: string): TipoDeImagen[] {
  if (esIdDeAdjunto(id)) return ['adjunto'];
  if (esIdDelMonton(id)) return ['monton'];
  if (esEntero(id)) return ['modelo'];
  return ORDEN.filter((t) => t !== 'adjunto' && t !== 'monton' && t !== 'modelo');
}

/**
 * Resuelve cada id contra las fuentes, en orden. Devuelve las que se encontraron (con su
 * tipo) y los ids que no eran de ninguna: el modelo tiene que saber cuáles no miró, en
 * vez de creer que vio menos fotos de las que pidió.
 *
 * Una fuente que tira se trata como "no es mío": un error de red en poses no puede
 * impedir que se encuentre una creación.
 */
export async function resolverImagenes(
  ids: readonly string[],
  fuentes: FuentesDeImagen,
): Promise<{ fotos: ImagenResuelta[]; inexistentes: string[] }> {
  const fotos: ImagenResuelta[] = [];
  const inexistentes: string[] = [];
  for (const id of ids) {
    let encontrada: ImagenResuelta | null = null;
    for (const tipo of candidatos(id)) {
      const fuente = fuentes[tipo];
      if (!fuente) continue;
      let r: RespuestaDeFuente = null;
      try {
        r = await fuente(id);
      } catch {
        r = null;
      }
      if (r) {
        encontrada = { id, url: r.url, tipo, ...(r.nombre ? { nombre: r.nombre } : {}) };
        break;
      }
    }
    if (encontrada) fotos.push(encontrada);
    else inexistentes.push(id);
  }
  return { fotos, inexistentes };
}

/**
 * Cómo se pasa esa imagen en `reference_images`. Va en el rótulo de cada foto: mirarla y
 * saber cómo usarla es la misma necesidad, y sin esto el agente tiene que acordarse del
 * parámetro que le toca a cada origen (que es justo lo que esta tool le evita).
 */
export function comoSePasaComoReferencia(img: ImagenResuelta): string {
  switch (img.tipo) {
    case 'adjunto':
      return `source "adjunto" + adjunto_id="${img.id}"`;
    case 'monton':
      return `source "adjunto" + foto_id="${img.id}"`;
    case 'producto':
      return `source "producto" + product_id="${img.id}"`;
    case 'modelo':
      return `source "modelo" + model_id="${img.id}"`;
    case 'generada':
      return `source "generada" + image_id="${img.id}"`;
    case 'pose':
      return `source "pose" + pose_id="${img.id}"`;
    case 'fondo':
      return `source "fondo" + fondo_id="${img.id}"`;
  }
}

/** Cómo se nombra cada tipo en el rótulo, en castellano. */
export const NOMBRE_DE_TIPO: Record<TipoDeImagen, string> = {
  adjunto: 'foto que subió el usuario',
  monton: 'foto del montón que adjuntó',
  producto: 'producto del catálogo',
  modelo: 'modelo guardada',
  generada: 'creación de este chat',
  pose: 'pose guardada',
  fondo: 'fondo guardado',
};
